import { bool, number } from "prop-types";
import React, { forwardRef, useMemo, useRef } from "react";
import { twMerge } from "tailwind-merge";

import RequestButton from "@components/shared/RequestButton";

import { useIsStickyTopOrBottom } from "@hooks/useIsStickyTopOrBottom";
import { useDetectDevice } from "@hooks/useDetectDevice";

const StickyRequestButton = forwardRef(function StickyRequestButton(
  props,
  ref
) {
  const {
    fixedHeaderHeight = 0,
    fixedFooterHeight = 0,
    isStickyToBottom = false,
    isStickyToTop = false,
    ...restProps
  } = props;

  const topThresholdRef = useRef(null);
  const bottomThresholdRef = useRef(null);
  const wrapRef = useRef(null);
  const containerHeight = useRef(0);

  const { isMobile } = useDetectDevice();

  const { stickToTop, stickToBottom } = useIsStickyTopOrBottom(
    isStickyToTop,
    isStickyToBottom,
    topThresholdRef,
    bottomThresholdRef,
    fixedHeaderHeight
  );

  const isSticking = isMobile && (stickToBottom || stickToTop);

  if (isSticking && wrapRef.current) {
    const { height } = wrapRef.current.getBoundingClientRect();
    containerHeight.current = height;
  }

  const style = useMemo(() => {
    if (isSticking) {
      return {
        bottom: stickToBottom ? `${fixedFooterHeight}px` : "auto",
        top: stickToTop ? `${fixedHeaderHeight}px` : "auto",
      };
    }

    return {};
  }, [
    fixedFooterHeight,
    fixedHeaderHeight,
    isSticking,
    stickToBottom,
    stickToTop,
  ]);

  return (
    <div className="flex flex-col items-center w-full">
      <div ref={topThresholdRef} />
      {isSticking && <div style={{ height: `${containerHeight.current}px` }} />}
      <div
        className={twMerge(
          "flex flex-col items-center w-full p-2 transition duration-300",
          isSticking
            ? "fixed bg-white/80 backdrop-blur-sm z-20"
            : "bg-transparent",
          isSticking && stickToTop && "shadow-md",
          isSticking && stickToBottom && "shadow--md"
        )}
        ref={wrapRef}
        style={style}
      >
        <RequestButton ref={ref} {...restProps} />
      </div>
      <div ref={bottomThresholdRef} />
    </div>
  );
});

StickyRequestButton.propTypes = {
  fixedFooterHeight: number,
  fixedHeaderHeight: number,
  isStickyToBottom: bool,
  isStickyToTop: bool,
};

export default StickyRequestButton;
